import { ethers } from "ethers";
import { DeviceAccount } from "../types";
import { DeviceError } from "../errors";
import { connectDevice, checkDevice } from "../usb/connection";
import { signAndSendEvmTx, updateEvmTx, buildEvmSignature } from "./signing";

/**
 * An ethers v6 AbstractSigner backed by a Keyfob hardware wallet.
 *
 * The app creates one instance per selected account and passes it to the
 * contracts lib — which never needs to know the signer is hardware-backed.
 *
 * Signing flow per sendTransaction call:
 *  1. ethers AbstractSigner.sendTransaction populates the tx (nonce, gas…)
 *  2. calls signTransaction below
 *  3. signTransaction serialises → sends to device → builds signature
 *  4. returns the signed serialised tx to ethers for broadcast
 *
 * On device error code UPDATE_SIGN_TX (307) the tx is refreshed and
 * re-submitted to the device once before propagating the error.
 */
export class KeyfobSigner extends ethers.AbstractSigner<ethers.Provider | null> {
  constructor(
    public readonly account: DeviceAccount,
    provider?: ethers.Provider,
    public readonly tokenBinary?: string,
  ) {
    super(provider ?? null);
  }

  async getAddress(): Promise<string> {
    return this.account.address;
  }

  async signTransaction(tx: ethers.TransactionRequest): Promise<string> {
    if (!checkDevice()) await connectDevice();

    const unsignedTx = ethers.Transaction.from(tx as ethers.TransactionLike);

    try {
      return await this._sign(unsignedTx);
    } catch (err: any) {
      if (err instanceof DeviceError && err.code === "UPDATE_SIGN_TX") {
        return await this._resign(unsignedTx);
      }
      throw err;
    }
  }

  private async _sign(tx: ethers.Transaction): Promise<string> {
    const serialized = ethers.getBytes(tx.unsignedSerialized);
    const signatureBytes = await signAndSendEvmTx(serialized, this.account.path, this.tokenBinary);
    const txHash = ethers.keccak256(tx.unsignedSerialized);
    tx.signature = buildEvmSignature(signatureBytes, txHash, this.account.address);
    return tx.serialized;
  }

  private async _resign(tx: ethers.Transaction): Promise<string> {
    if (!this.provider) throw new DeviceError("FAILED_SIGN_TX", "No provider for tx refresh.");

    const feeData = await this.provider.getFeeData();
    tx.nonce = await this.provider.getTransactionCount(this.account.address);
    tx.maxFeePerGas = feeData.maxFeePerGas ?? BigInt("100000000000");
    tx.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas ?? BigInt("2000000000");

    const serialized = ethers.getBytes(tx.unsignedSerialized);
    // Carry the token record through the retry. Without it the device is re-asked to sign with
    // no token identity supplied, and a tx that displayed "VIP" on the first attempt could be
    // approved blind on the second.
    const signatureBytes = await updateEvmTx(serialized, this.tokenBinary);
    const txHash = ethers.keccak256(tx.unsignedSerialized);
    tx.signature = buildEvmSignature(signatureBytes, txHash, this.account.address);
    return tx.serialized;
  }

  async signMessage(_message: string | Uint8Array): Promise<string> {
    throw new DeviceError("NOT_SUPPORTED", "Message signing is not supported on the Keyfob.");
  }

  async signTypedData(
    _domain: ethers.TypedDataDomain,
    _types: Record<string, ethers.TypedDataField[]>,
    _value: Record<string, any>,
  ): Promise<string> {
    throw new DeviceError("NOT_SUPPORTED", "Typed-data signing is not supported on the Keyfob.");
  }

  connect(provider: ethers.Provider): KeyfobSigner {
    return new KeyfobSigner(this.account, provider, this.tokenBinary);
  }
}
